import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Input } from './';
import { numberFormatter } from '../styles/GlobalStyles';
import '../styles/PackagePriceEditor.css';

// Editable package (portfolio) price. This is the SINGLE source of truth for
// what buyers see and what's sent to the CRM (portfolio.combinedAskingPrice →
// GHL "Price" + monetaryValue). Editing an individual house's price does NOT
// change it — hence this dedicated, clearly-labelled control.
//
// Wraps the existing managePortfolio 'update' action (auth-derived companyId, so
// none is passed). A successful save bubbles the new value up via onUpdated so
// the parent's local `portfolio` state — which the CRM send path reads — stays
// in sync without a refetch.
//
// Props:
//   portfolio        — { portfolioId, combinedAskingPrice, ... }
//   onUpdated(price) — called with the new integer price after a successful save

const stripToInt = (val) => {
    const cleaned = String(val ?? '').replace(/[^0-9]/g, '');
    return cleaned === '' ? null : parseInt(cleaned, 10);
};

const toFormatted = (val) => {
    const n = stripToInt(val);
    return n === null ? '' : numberFormatter.format(n);
};

function PackagePriceEditor({ portfolio, onUpdated }) {
    const portfolioId = portfolio?.portfolioId;
    const savedPrice = portfolio?.combinedAskingPrice ?? null;

    const [priceInput, setPriceInput] = useState(toFormatted(savedPrice));
    const [status, setStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
    const [errorMsg, setErrorMsg] = useState(null);
    const savedTimerRef = useRef(null);

    // Reseed when the underlying portfolio price changes (e.g. parent refetch or
    // a sibling page's edit) — but never clobber what the user is mid-edit.
    useEffect(() => {
        setPriceInput(toFormatted(savedPrice));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [savedPrice, portfolioId]);

    useEffect(() => () => { if (savedTimerRef.current) clearTimeout(savedTimerRef.current); }, []);

    const handleChange = useCallback((e) => {
        setPriceInput(toFormatted(e.target.value));
        if (status === 'error') { setStatus('idle'); setErrorMsg(null); }
    }, [status]);

    const save = useCallback(async () => {
        if (!portfolioId) return;
        const parsed = stripToInt(priceInput);

        // Require a value — blank or non-positive is rejected; revert to the last
        // saved price so the field never shows an unsaved invalid state.
        if (parsed === null || parsed <= 0) {
            setErrorMsg('Enter a package price');
            setStatus('error');
            setPriceInput(toFormatted(savedPrice));
            return;
        }
        const prev = savedPrice;
        if (parsed === prev) { setStatus('idle'); setErrorMsg(null); return; }

        // Optimistically push the new price to the parent BEFORE the network call.
        // The CRM send path reads the price from the parent's local `portfolio`
        // state and puts it directly in the payload, so without this an edit
        // followed by an immediate "Create Opportunity" (while this slow save —
        // it re-matches siblings server-side — is still in flight) would send the
        // OLD price. On failure we roll the parent back so state matches Firestore.
        setStatus('saving'); setErrorMsg(null);
        if (onUpdated) onUpdated(parsed);
        try {
            const managePortfolioFn = httpsCallable(getFunctions(), 'managePortfolio');
            const res = await managePortfolioFn({
                action: 'update', portfolioId, combinedAskingPrice: parsed,
            });
            if (res.data?.status === 200) {
                setStatus('saved');
                if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
                savedTimerRef.current = setTimeout(() => setStatus('idle'), 3000);
            } else {
                if (onUpdated) onUpdated(prev);
                setPriceInput(toFormatted(prev));
                setErrorMsg(res.data?.error || 'Failed to save price');
                setStatus('error');
            }
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Failed to update package price:', err);
            if (onUpdated) onUpdated(prev);
            setPriceInput(toFormatted(prev));
            setErrorMsg('An error occurred');
            setStatus('error');
        }
    }, [portfolioId, priceInput, savedPrice, onUpdated]);

    return (
        <div className="package-price-editor">
            <div className="package-price-editor-head">
                <span className="body-semibold colour-primary">Package price</span>
                <span className="label-regular colour-secondary">Sent to buyers &amp; your CRM</span>
            </div>
            <div className="package-price-editor-control">
                <Input
                    label={null}
                    labelType={null}
                    value={priceInput}
                    onChange={handleChange}
                    onBlur={save}
                    placeholder="0"
                    type="text"
                    inputMode="numeric"
                    leftUnit="$"
                    rightUnit={null}
                    unitSwitch={false}
                    leftUnitSelected={true}
                    unitSwitchFunc={null}
                    disabled={status === 'saving'}
                    multiline={false}
                    error={status === 'error'}
                    onMouseEnter={null}
                    onMouseLeave={null}
                    reset={false}
                    resetFunc={null}
                    resetPosition={null}
                    focusMultiline={false}
                    prompt={false}
                    submitPromptDisabled={false}
                    submitPrompt={null}
                    onHeightChange={null}
                />
                <span className="package-price-editor-status">
                    {status === 'saving' && <span className="label-regular colour-secondary">Saving…</span>}
                    {status === 'saved' && <span className="label-regular colour-green">Saved</span>}
                    {status === 'error' && <span className="label-regular colour-error">{errorMsg}</span>}
                </span>
            </div>
        </div>
    );
}

export default PackagePriceEditor;
